import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { SubjectKind } from '@hp/domain';
import { trpcClient } from '../../api/trpc';
import { invalidateEnrichment } from '../enrichment/queries';
import { invalidateMerge } from '../jobs/queries';
import { invalidatePublish } from '../publish/queries';
import { invalidateTrpcPipeline } from './queries';
import type { PipelineNextActionKind } from './queries';

function assertNever(kind: never): never {
  throw new Error(`Unhandled pipeline action: ${String(kind)}`);
}

// --- shared action executor: the banner and the per-stage buttons run the same logic ---

export type PipelineActionInput = { kind: PipelineNextActionKind; entityType?: SubjectKind };

export function usePipelineAction() {
  const { t } = useTranslation(['pipeline', 'common']);
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async (action: PipelineActionInput): Promise<string | null> => {
      switch (action.kind) {
        case 'start_sync':
          await trpcClient.pipedrive.startSync.mutate({ kind: 'full' });
          return null;
        case 'start_delta':
          await trpcClient.pipedrive.startSync.mutate({ kind: 'delta' });
          return null;
        case 'start_backup':
          await trpcClient.pipedrive.startSync.mutate({ kind: 'backup' });
          return null;
        case 'run_enrichment':
          // the guided pipeline only ever runs the free registry step; paid providers start from the enrichment page
          await trpcClient.enrichment.startRun.mutate({ provider: 'ares' });
          return null;
        case 'create_publish_dry':
          await trpcClient.publish.createJob.mutate({ mode: 'dry_run' });
          return '/crm-data/enrichment#publish';
        case 'create_publish_live':
          await trpcClient.publish.createJob.mutate({ mode: 'live' });
          return '/crm-data/enrichment#publish';
        case 'run_dedup':
          await trpcClient.dedup.startRun.mutate({ entityType: action.entityType ?? 'organization' });
          return null;
        case 'create_dry_run':
        case 'create_pilot':
        case 'create_full': {
          const mode = action.kind === 'create_dry_run' ? 'dry_run' : action.kind === 'create_pilot' ? 'pilot' : 'full';
          const res = await trpcClient.merge.createJob.mutate({
            mode,
            entityType: action.entityType ?? 'organization',
          });
          return `/crm-data/jobs/${res.id}`;
        }
        // navigation-only kinds never reach the mutation (execute() handles them)
        case 'review':
        case 'review_enrichment':
        case 'inspect_dry_run':
        case 'wait':
        case 'all_done':
          return null;
        default:
          return assertNever(action.kind);
      }
    },
    onSuccess: async (navTarget) => {
      // awaited: the button stays busy until the pipeline state already shows the new run
      await Promise.all([invalidateTrpcPipeline(), invalidateMerge(), invalidateEnrichment(), invalidatePublish()]);
      if (navTarget) void navigate(navTarget);
    },
  });

  function execute(action: PipelineActionInput) {
    switch (action.kind) {
      case 'review': {
        const params = new URLSearchParams({ status: 'pending' });
        if (action.entityType) params.set('entityType', action.entityType);
        void navigate(`/crm-data/deduplication?${params.toString()}`);
        return;
      }
      case 'review_enrichment':
        void navigate('/crm-data/enrichment?status=pending');
        return;
      case 'inspect_dry_run':
        void navigate('/crm-data/jobs');
        return;
      case 'create_publish_live':
        if (!window.confirm(t('confirm.publishLive'))) return;
        break;
      case 'create_pilot':
        if (!window.confirm(t('confirm.pilot'))) return;
        break;
      case 'create_full':
        if (!window.confirm(t('confirm.full'))) return;
        break;
      case 'wait':
      case 'all_done':
        return;
      default:
        break;
    }
    mutation.mutate(action);
  }

  /** True for the one action whose request is in flight (its button pulses; the others are merely disabled). */
  function isBusy(action: PipelineActionInput): boolean {
    const v = mutation.variables;
    return (
      mutation.isPending &&
      v !== undefined &&
      v.kind === action.kind &&
      (v.entityType ?? 'organization') === (action.entityType ?? 'organization')
    );
  }

  return { execute, isBusy, isPending: mutation.isPending, error: mutation.error };
}
